import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { WEEKDAYS, sortByPeriod } from "@/components/Timetable";

const DAY_KEYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** Month grid: each school day lists the periods that meet that weekday; non-school days come from the school calendar. */
export function ScheduleMonth({ classes, onSelect, testid = "schedule-month" }) {
  const [cursor, setCursor] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [offDays, setOffDays] = useState({});
  const monthKey = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;

  useEffect(() => {
    const from = `${monthKey}-01`, to = `${monthKey}-31`;
    api.get("/calendar", { params: { from, to } })
      .then(({ data }) => setOffDays(Object.fromEntries(data.map((e) => [e.date, e]))))
      .catch(() => setOffDays({}));
  }, [monthKey]);

  const cells = useMemo(() => {
    const first = new Date(cursor), last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const lead = (first.getDay() + 6) % 7;
    const out = [];
    for (let i = 0; i < lead; i++) out.push(null);
    for (let d = 1; d <= last.getDate(); d++) out.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    while (out.length % 7) out.push(null);
    return out;
  }, [cursor]);

  const byDay = useMemo(() => Object.fromEntries(WEEKDAYS.map((d) => [d, sortByPeriod(classes.filter((c) => (c.days || WEEKDAYS).includes(d)))])), [classes]);
  const today = iso(new Date());
  const title = cursor.toLocaleDateString([], { month: "long", year: "numeric" });

  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <button type="button" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid={`${testid}-prev`} className="h-8 w-8 rounded-lg border border-slate-200 bg-white inline-flex items-center justify-center text-slate-600 hover:bg-slate-100"><ChevronLeft className="h-4 w-4" /></button>
        <div className="font-display font-semibold text-slate-900 w-44 text-center" data-testid={`${testid}-title`}>{title}</div>
        <button type="button" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid={`${testid}-next`} className="h-8 w-8 rounded-lg border border-slate-200 bg-white inline-flex items-center justify-center text-slate-600 hover:bg-slate-100"><ChevronRight className="h-4 w-4" /></button>
        <button type="button" onClick={() => { const n = new Date(); setCursor(new Date(n.getFullYear(), n.getMonth(), 1)); }} data-testid={`${testid}-today`} className="ml-1 h-8 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 hover:bg-slate-100">Today</button>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50/60">
          {[...WEEKDAYS, "Sat", "Sun"].map((d) => <div key={d} className={`px-2 py-2 text-[11px] font-bold uppercase tracking-widest ${d === "Sat" || d === "Sun" ? "text-slate-300" : "text-slate-500"}`}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d, i) => {
            if (!d) return <div key={i} className="min-h-24 border-b border-r border-slate-100 bg-slate-50/40" />;
            const key = DAY_KEYS[d.getDay()], dateKey = iso(d), weekend = key === "Sat" || key === "Sun", off = offDays[dateKey];
            const isToday = dateKey === today;
            const list = weekend || off ? [] : byDay[key] || [];
            return (
              <div key={i} data-testid={`${testid}-day-${dateKey}`} className={`min-h-24 border-b border-r border-slate-100 p-1.5 ${weekend ? "bg-slate-50/40" : ""} ${isToday ? "bg-primary/5" : ""}`}>
                <div className={`text-[11px] font-semibold mb-1 inline-flex h-5 w-5 items-center justify-center rounded-full ${isToday ? "bg-primary text-white" : weekend ? "text-slate-300" : "text-slate-600"}`}>{d.getDate()}</div>
                {off && <div className="rounded bg-rose-50 border border-rose-100 px-1.5 py-0.5 text-[10px] font-medium text-rose-700 truncate" title={off.label} data-testid={`${testid}-off-${dateKey}`}>{off.label}</div>}
                <div className="space-y-0.5">
                  {list.map((c) => (
                    <button key={c.id} type="button" onClick={() => onSelect?.(c)} data-testid={`${testid}-cell-${dateKey}-${c.id}`}
                      className="w-full text-left rounded bg-blue-50 hover:bg-blue-100 px-1.5 py-0.5 text-[10px] leading-tight text-slate-800 truncate transition">
                      <b className="text-primary">{(c.period || "HR").replace(/^Period\s*/i, "P")}</b> {c.name}{c.start_time ? <span className="text-slate-400"> · {c.start_time}</span> : null}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
